import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../api";

const ChevronLeft = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2c3e50"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const ChevronRight = () => (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#2c3e50"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="9 18 15 12 9 6" />
  </svg>
);

const AdsSlider = () => {
  const [ads, setAds] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [isHovering, setIsHovering] = useState(false);

  // ================================
  // FETCH DESKTOP ADS
  // ================================
  useEffect(() => {
    const fetchAds = async () => {
      try {
        const res = await axios.get(`${API_URL}/ads/desktop`);

        if (res.data.success) {
          setAds(res.data.ads || []);
          setCurrentPage(0);
        } else {
          setAds([]);
        }
      } catch (error) {
        console.error("Failed to fetch desktop ads:", error);
        setAds([]);
      }
    };

    fetchAds();
  }, []);

  // ================================
  // AUTO SLIDE
  // ================================
  useEffect(() => {
    if (ads.length <= 1 || isHovering) return;

    const interval = setInterval(() => {
      setCurrentPage((prev) =>
        prev >= ads.length - 1 ? 0 : prev + 1
      );
    }, 3500);

    return () => clearInterval(interval);
  }, [ads.length, isHovering]);

  // ================================
  // NEXT SLIDE
  // ================================
  const nextSlide = () => {
    if (ads.length <= 1) return;

    setCurrentPage((prev) =>
      prev >= ads.length - 1 ? 0 : prev + 1
    );
  };

  // ================================
  // PREVIOUS SLIDE
  // ================================
  const prevSlide = () => {
    if (ads.length <= 1) return;

    setCurrentPage((prev) =>
      prev <= 0 ? ads.length - 1 : prev - 1
    );
  };

  // ================================
  // NO ADS
  // ================================
  if (ads.length === 0) return null;

  return (
    <div
      style={styles.wrapper}
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      {/* ================================
          SLIDER TRACK
      ================================= */}
      <div
        style={{
          ...styles.track,
          transform: `translateX(-${currentPage * 100}%)`,
        }}
      >
        {ads.map((ad, index) => (
          <div
            key={ad._id || ad.id || index}
            style={styles.page}
          >
            <div style={styles.adCard}>
              <img
                src={ad.image}
                alt={ad.title || "Advertisement"}
                style={styles.adImage}
                draggable={false}
              />
            </div>
          </div>
        ))}
      </div>

      {/* ================================
          LEFT / RIGHT ARROWS
      ================================= */}
      {ads.length > 1 && (
        <>
          <button
            type="button"
            onClick={prevSlide}
            aria-label="Previous advertisement"
            style={{
              ...styles.arrow,
              ...styles.leftArrow,
            }}
          >
            <ChevronLeft />
          </button>

          <button
            type="button"
            onClick={nextSlide}
            aria-label="Next advertisement"
            style={{
              ...styles.arrow,
              ...styles.rightArrow,
            }}
          >
            <ChevronRight />
          </button>
        </>
      )}

      {/* ================================
          DOTS
      ================================= */}
      {ads.length > 1 && (
        <div style={styles.dots}>
          {ads.map((_, index) => (
            <button
              key={index}
              type="button"
              onClick={() => setCurrentPage(index)}
              aria-label={`Go to advertisement ${index + 1}`}
              style={{
                ...styles.dot,
                ...(currentPage === index
                  ? styles.activeDot
                  : styles.inactiveDot),
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default AdsSlider;

// ======================================================
// STYLES
// ======================================================

const styles = {
  // ================================
  // MAIN ADS SECTION
  // ================================
  wrapper: {
    width: "100%",
    position: "relative",
    overflow: "hidden",
    background: "#8ec5fc",
    boxSizing: "border-box",

    // Increased vertical space
    padding: "18px 0 32px",
  },

  // ================================
  // SLIDER TRACK
  // ================================
  track: {
    width: "100%",
    display: "flex",
    transition:
      "transform 0.65s cubic-bezier(0.25, 1, 0.5, 1)",
    willChange: "transform",
  },

  // ================================
  // EACH SLIDE
  // ================================
  page: {
    minWidth: "100%",
    width: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: "border-box",
  },

  // ================================
  // AD CARD
  // ================================
  adCard: {
    width: "90%",

    // Keeps desktop banner ratio
    aspectRatio: "3.40 / 1",

    position: "relative",
    overflow: "hidden",
    boxSizing: "border-box",

    background: "#4777c3",
    border: "1px solid #1c3b65",
    borderRadius: "40px",
  },

  // ================================
  // AD IMAGE
  // ================================
  adImage: {
    width: "100%",
    height: "100%",
    display: "block",

    // Full card coverage
    objectFit: "cover",
    objectPosition: "center center",

    borderRadius: "40px",

    userSelect: "none",
    WebkitUserDrag: "none",
  },

  // ================================
  // ARROWS
  // ================================
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",

    zIndex: 20,

    width: "42px",
    height: "42px",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    padding: 0,

    background: "rgba(255,255,255,0.95)",
    border: "1px solid rgba(255,255,255,0.8)",
    borderRadius: "50%",

    cursor: "pointer",

    boxShadow: "0 4px 14px rgba(0,0,0,0.18)",

    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },

  // ================================
  // LEFT ARROW
  // ================================
  leftArrow: {
    left: "28px",
  },

  // ================================
  // RIGHT ARROW
  // ================================
  rightArrow: {
    right: "28px",
  },

  // ================================
  // DOT CONTAINER
  // ================================
  dots: {
    position: "absolute",

    left: "50%",
    bottom: "12px",

    transform: "translateX(-50%)",

    zIndex: 25,

    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    gap: "8px",

    padding: "6px 10px",

    borderRadius: "999px",

    background: "rgba(0,0,0,0.20)",

    backdropFilter: "blur(6px)",
    WebkitBackdropFilter: "blur(6px)",
  },

  // ================================
  // DOT BASE
  // ================================
  dot: {
    padding: 0,
    border: "none",

    cursor: "pointer",

    transition: "all 0.3s ease",
  },

  // ================================
  // ACTIVE DOT
  // ================================
  activeDot: {
    width: "22px",
    height: "6px",

    borderRadius: "999px",

    background: "#ffffff",
  },

  // ================================
  // INACTIVE DOT
  // ================================
  inactiveDot: {
    width: "7px",
    height: "7px",

    borderRadius: "50%",

    background: "rgba(255,255,255,0.65)",
  },
};